import { i18n } from '@/commons/utils';
import { updateCreateProcess, disableForm, updatePrivacies } from './create-account';
import {
  getStoredIdentifier,
  setStoredIdentifier,
  ajaxError,
  disableCompanyCheckbox,
  setCustomerType,
  setPersonId,
  setSmartId,
  disableSubmit,
  displayOptions
} from '../customer-utils';

import { init as initOptions } from './create-account-options';

const services = {
  IDENTIFIER_EXISTS: 'rest/model/atg/userprofiling/ProfileActor/isIdentifierExists',
  SEND_VALIDATION_CODE: 'rest/model/atg/userprofiling/ProfileActor/send-mobile-verification-code',
};

let identifier;
let countryCode;
let confirmation;
let validationButton;
let counters;
let mobileSecondIdentifier;
let mailSecondIdentifier;
let isPrefilled;

export const init = form => {
  identifier = cube.$('#js-identifier input:not([type="hidden"])');
  countryCode = cube.$('#js-identifier [name="mobileCountryCode"]');
  confirmation = cube.$id('js-email-main-identifier-confirmation');
  validationButton = cube.$id('js-login-button');
  counters = cube.$$('.js-resend-code-button');
  mobileSecondIdentifier = cube.$('#js-personal-data-mobile #mobile');
  mailSecondIdentifier = cube.$id('js-email-second-identifier');
  isPrefilled = cube.$id('js-is-prefilled').value === 'true';

  setIdentifier(form);
  initOptions(form);

  identifier.on('input', () => validate(form));

  if (countryCode) countryCode.on('change', () => validate(form));

  if (confirmation) {
    confirmation.on('input', () => validate(form));
    ['copy', 'paste'].forEach(event => confirmation.on(event, e => disableCopyPaste(e, form)));
  }

  form.on('keypress', event => {
    if (validationButton.offsetParent && event.keyCode === 13) {
      event.preventDefault();
      validationButton.dispatchEvent(new CustomEvent('click'));
    }
  });

  counters.forEach(counter => {
    const $counter = $(counter);
    $counter.data('countdown', new window.decathlon.userAccount.Countdown($counter, 30));
  });

  if (mobileSecondIdentifier && cube.$id('js-personal-data-mobile').dataset.secondIdentifier) {
    validateMobileSecondIdentifier(mobileSecondIdentifier, form);
    mobileSecondIdentifier.on('input', () => validateMobileSecondIdentifier(mobileSecondIdentifier, form));

    const countryCodeSecondIdentifier = cube.$('#js-personal-data-mobile [name="mobileCountryCode"]');

    if (countryCodeSecondIdentifier)
      countryCodeSecondIdentifier.on('change', () =>
        validateMobileSecondIdentifier(mobileSecondIdentifier, form, true)
      );
  }

  if (mailSecondIdentifier)
    mailSecondIdentifier.on('input', () => validateMailSecondIdentifier(mailSecondIdentifier, form));

  const resendCodeButtons = cube.$$('.js-resend-code-button');
  if (resendCodeButtons.length) resendCodeButtons.on('click', resendValidationCode);

  const sendCodeButton = cube.$id('js-send-code-button');
  if (sendCodeButton) sendCodeButton.on('click', sendSecondIdentifierValidationCode);

  validationButton.on('click', () => checkIdentifierAvailaibility(validationButton, form));
};

/**
 * Disable copy/paste for email confirmation input and display a custom error if a customer tries to do it.
 *
 * @param {Event} event
 * @param {HTMLFormElement} form The create account form.
 */
const disableCopyPaste = (event, form) => {
  event.preventDefault();

  const validator = $(form).validate();
  const callback = validator.settings.onkeyup.bind(validator);

  validator.settings.onkeyup = (element, e) => {
    if (element === confirmation) return;

    callback(element, e);
  };

  const error = {
    [confirmation.name]:
      event.type === 'copy' ? i18n('profile.update.mail.copy.forbidden') : i18n('profile.update.mail.paste.forbidden'),
  };

  validator.showErrors(error);
};

/**
 * Fill and revalidate the login field if you're coming from the login page.
 *
 * @param {HTMLFormElement} form The create account form.
 */
const setIdentifier = form => {
  const data = getStoredIdentifier('MAIN');

  if (identifier.value || (data && data.login)) {
    identifier.value = identifier.value || data.login;

    if (data.identifierType === 'MOBILE') {
      $('[name="mobileCountryCode"]').selectpicker('val', data.countryCode);

      if (identifier.classList.contains('js-login-email') && mobileSecondIdentifier)
        mobileSecondIdentifier.value = data.login;
    }

    if (!$(identifier).valid()) {
      identifier.value = '';
      identifier.closest('.form-group').classList.remove('has-error');
      disableForm(form);

      return;
    }

    if (!confirmation) checkIdentifierAvailaibility(validationButton, form, data);
    else disableForm(form);
  } else {
    disableForm(form);
  }
};

/**
 * Validate the login and display the verification button.
 *
 * @param {HTMLFormElement} form The create account form.
 */
const validate = form => {
  disableForm(form);
  displayOptions(false);
  displayOptions(false, 'SECOND');

  if ($(identifier).valid() && (!confirmation || (confirmation.value.length && $(confirmation).valid()))) {
    displayValidationButton();
  } else {
    displayMobileValidation(false);
    displayValidationButton(false);
  }
};

/**
 * Update the customer's person id.
 *
 * @param {HTMLFormElement} form The create account form.
 * @param {String} identiferType 'MAIN' or 'SECOND' identifier.
 */
const updatePersonId = (form, identiferType) => {
  const data = getStoredIdentifier(identiferType);

  setPersonId(data.personId);
  disableForm(form, false);
};

/**
 * Display or hide the login validation button.
 *
 * @param {Boolean} displayed
 */
const displayValidationButton = (displayed = true) => {
  const { classList } = validationButton;

  if (displayed) classList.remove('hidden');
  else classList.add('hidden');
};

/**
 * Send an XHR to check if the main identifier is not already linked to another account.
 *
 * @param {HTMLButtonElement} button The login validation button.
 * @param {HTMLFormElement} form The create account form.
 * @param {HTMLFormElement} data The customer data saved in local storage, usefull for the automatic validation.
 */
const checkIdentifierAvailaibility = (button, form, data = {}) => {
  const params = Object.assign(data, {
    login: identifier.value,
    countryCode: countryCode && countryCode.value,
    isCheckout: cube.$id('is-checkout').value,
  });

  if (button.disabled) return;

  button.disabled = true;

  cube.ajax
    .post(services.IDENTIFIER_EXISTS, params)
    .onSuccess(response => {
      checkIdentifierAvailaibilitySuccess(response, button, form, params);
    })
    .onError(response => {
      ajaxError(response, button);
    })
    .call();
};

/**
 * Do some actions depending on whether the identifier exists or not.
 *
 * @param {Object} response The XHR response.
 * @param {HTMLButtonElement} button The login validation button.
 * @param {HTMLFormElement} form The create account form.
 * @param {Object} params The current identifer data.
 */
const checkIdentifierAvailaibilitySuccess = (response, button, form, params) => {
  if (ajaxError(response, button)) return;

  button.disabled = false;

  const { data } = response.responseTO;
  const { memberType, personId, identifierType, displayPassword, identifierExists, personType } = data;
  setStoredIdentifier(Object.assign(params, { memberType, personId, identifierType }), 'MAIN');

  displayValidationButton(false);
  displaySecondIdentifier(identifierType);
  updateCreateProcess(displayPassword, memberType);
  updatePrivacies(identifierExists, memberType);
  if (!isPrefilled) setPersonId('');
  setSmartId('');
  setCustomerType(1);

  disableCompanyCheckbox(isPrefilled || params.continueWithoutLogIn || false);

  if (mobileSecondIdentifier && mobileSecondIdentifier.closest('.form-group').classList.contains('validated'))
    mobileSecondIdentifier.dispatchEvent(new CustomEvent('input'));

  if (params.continueWithoutLogIn) {
    setCustomerType(personType);
    setSmartId(personId);
    return;
  }

  /* Specific for prefilled account in China
   * COUNTRY is an environement variable
   */
  if (cube.env.country === 'CN' && isPrefilled && identifierType === 'MOBILE') {
    displayMobileValidation(true, params);
    return;
  }

  if (isPrefilled) return;

  if (identifierExists && memberType === 1 && identifierType === 'MOBILE') {
    displayMobileValidation(true, params);
  } else if (identifierExists) {
    if (data.options.length) displayOptions(true, 'MAIN', data.options);

    dispatchActions(data.actions, form, 'MAIN');
  } else {
    disableForm(form, false);

    if (identifierType === 'MOBILE') displayMobileValidation(true, params);
  }
};

/**
 * Dispatch action(s) if the customer's identifier already exists.
 *
 * @param {Array<String>} actions A list of actions.
 * @param {HTMLFormElement} form The create account form.
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const dispatchActions = (actions, form, identifierType) => {
  const ACTIONS = {
    'disable-form': () => disableForm(form, true),
    'disable-submit': () => disableSubmit(true, 'CREATE'),
    update: () => updatePersonId(form, identifierType),
  };

  actions.forEach(action => ACTIONS[action]());
};

/**
 * Display the second identifier field if needed and depending on the main identifier type.
 *
 * @param {String} identiferType The main identifier type.
 */
const displaySecondIdentifier = identiferType => {
  const email = cube.$id('js-personal-data-email');
  const mobile = cube.$id('js-personal-data-mobile');

  if (!email || !mobile) return;

  if (identiferType === 'MOBILE') {
    email.classList.remove('hidden');
    mobile.classList.add('hidden');
  } else {
    email.classList.add('hidden');
    mobile.classList.remove('hidden');

    if (isPrefilled && mobileSecondIdentifier.value) mobileSecondIdentifier.dispatchEvent(new CustomEvent('input'));
  }
};

/**
 * Validate the second identifier if it's a mobile.
 *
 * @param {HTMLFormElement} form The create account form.
 * @param {HTMLElement} secondIdentifier The second identifier.
 * @param {Boolean} validation true if the second identifier need a validation. (usefull when the country code is update).
 */
let timeout;
const validateMobileSecondIdentifier = (secondIdentifier, form, validation) => {
  const data = getStoredIdentifier('MAIN');
  const countryCodesecondIdentifier = cube.$('#js-personal-data-mobile [name="mobileCountryCode"]');
  const { regex } =
    countryCodesecondIdentifier.tagName === 'INPUT'
      ? countryCodesecondIdentifier.dataset
      : countryCodesecondIdentifier[countryCodesecondIdentifier.selectedIndex].dataset;

  if (validation) $(secondIdentifier).valid();

  displayOptions(false, 'SECOND');
  displayMobileValidation(false, null, 'SECOND');
  displaySecondIdentifierMobileValidationButton(false);
  disableSubmit(false, 'CREATE');

  if (RegExp(regex).test(secondIdentifier.value)) {
    const params = {
      login: secondIdentifier.value,
      countryCode: countryCodesecondIdentifier && countryCodesecondIdentifier.value,
      isCheckout: cube.$id('is-checkout').value,
      mainMemberType: data.memberType || 0,
    };

    $(secondIdentifier).valid();
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      checkSecondIdentifierAvailaibility(secondIdentifier, form, params);
    }, 500);
  }
};

/**
 * Validate the second identifier if it's an email.
 *
 * @param {HTMLFormElement} form The create account form.
 * @param {HTMLElement} secondIdentifier The second identifier.
 */
const validateMailSecondIdentifier = (secondIdentifier, form) => {
  const data = getStoredIdentifier('MAIN');

  displayOptions(false, 'SECOND');
  disableSubmit(false, 'CREATE');

  const params = {
    login: secondIdentifier.value,
    isCheckout: cube.$id('is-checkout').value,
    mainMemberType: data.memberType || 0,
  };

  clearTimeout(timeout);

  if ($(secondIdentifier).valid()) {
    timeout = setTimeout(() => {
      checkSecondIdentifierAvailaibility(secondIdentifier, form, params);
    }, 500);
  }
};

/**
 * Send an XHR to check if the second identifier is not already linked to another account.
 *
 * @param {HTMLInputElement} field The second identifier field.
 * @param {HTMLFormElement} form The create account form.
 * @param {Object} params The current identifer data.
 */
const checkSecondIdentifierAvailaibility = (field, form, params) => {
  cube.ajax
    .post(services.IDENTIFIER_EXISTS, params)
    .onSuccess(response => {
      checkSecondIdentifierAvailaibilitySuccess(response, field, form, params);
    })
    .onError(response => {
      ajaxError(response);
    })
    .call();
};

/**
 * Do some actions depending on whether the identifier exists or not.
 *
 * @param {Object} response The XHR response.
 * @param {HTMLInputElement} field The second identifier field.
 * @param {HTMLFormElement} form The create account form.
 * @param {Object} params The current identifer data.
 */
const checkSecondIdentifierAvailaibilitySuccess = (response, field, form, params) => {
  const { data, errors } = response && response.responseTO;

  setStoredIdentifier(params, 'SECOND');

  // Identifier is already used on SEVERAL accounts
  if (!data && errors && errors.find(item => item.code === 'account.create.duplicateNumber')) {
    disableSubmit(true, 'CREATE');
    cube.alert.error(i18n('account.create.duplicateNumber'));
  }

  // Identifier is already used on ONE account
  else if (data && data.identifierExists) {
    dispatchActions(data.actions, form, 'SECOND');
    if (data.options.length) displayOptions(true, 'SECOND', data.options);
  }

  // Identifier is available
  else {
    if(data.identifierType === 'EMAIL') {
      displaySecondIdentifierMobileValidationButton(false);
    }else {
      displaySecondIdentifierMobileValidationButton();
    }
    disableSubmit(false, 'CREATE');
  }
};

/**
 * Display or hide the validation code process for mobile identifier.
 *
 * @param {Boolean} displayed Display or hide the validation process.
 * @param {Object} params The current identifer data.
 * @param {String} identiferType 'MAIN' or 'SECOND' identifier.
 */
export const displayMobileValidation = (displayed, params, identifierType = 'MAIN') => {
  const phoneValidation =
    identifierType === 'MAIN' ? cube.$id('js-phone-validation') : cube.$id('js-second-identifier-phone-validation');

  if (!phoneValidation) return;

  if (displayed) {
    phoneValidation.classList.remove('hidden');
    phoneValidation.classList.add('account-form__field--disabled');
    phoneValidation.$('.js-resend-code-button').style.display = 'none';
    sendValidationCode(params, phoneValidation);
  } else {
    phoneValidation.classList.add('hidden');
    phoneValidation.classList.remove('account-form__field--disabled');
    counters.forEach(counter =>
      $(counter)
        .data('countdown')
        .stop()
    );
  }
};

/**
 * Display or hide the second identifier mobile verification button if it's activated.
 *
 * @param {Boolean} displayed Dispaly or hide the button.
 */
const displaySecondIdentifierMobileValidationButton = (displayed = true) => {
  const validation = cube.$id('js-mobile-validation');

  if (!validation) return;

  if (displayed) validation.classList.remove('hidden');
  else validation.classList.add('hidden');
};

/**
 * Display the mobile validation input for the second identifier
 *
 * @param {Event} event
 */
const sendSecondIdentifierValidationCode = event => {
  event.preventDefault();

  const data = getStoredIdentifier('SECOND');

  displaySecondIdentifierMobileValidationButton(false);
  displayMobileValidation(true, data, 'SECOND');
};

/**
 * Send an XHR to send an SMS with a validation code to the current mobile identifier.
 *
 * @param {Object} params The current identifer data.
 * @param {HTMLInputElement} input The phone validation input.
 */
const sendValidationCode = (params, input) => {
  cube.ajax
    .post(services.SEND_VALIDATION_CODE, params)
    .onSuccess(response => sendValidationCodeSuccess(response, input))
    .onError(ajaxError)
    .call();
};

/**
 * Display a message and launch a counter if the XHR returns no error(s).
 *
 * @param {Object} response The XHR response.
 */
const sendValidationCodeSuccess = (response, input) => {
  if (ajaxError(response)) return;

  input.classList.remove('account-form__field--disabled');
  counters.forEach(counter =>
    $(counter)
      .data('countdown')
      .go()
  );
  cube.alert.information(i18n('profile.smsSent'));
};

/**
 * Resend a new validation Code
 *
 * @param {Event} event
 */
function resendValidationCode(event) {
  event.preventDefault();

  const identiferType = this.dataset.identifierType;
  const data = getStoredIdentifier(identiferType);

  displayMobileValidation(true, data, identiferType);
}

export const rules = {
  rules: {
    login: {
      maxlength: 100,
      mobile: {
        depends: element => element.classList.contains('js-login-mobile'),
      },
      email: {
        depends: element => element.classList.contains('js-login-email'),
      },
      emailmobile: {
        depends: element => element.classList.contains('js-login-email-mobile'),
      },
    },

    'email-confirmation': {
      equalTo: element => `#${element.dataset.equalTo}`,
    },

    mobile: {
      mobile: true,
    },
  },
};



// WEBPACK FOOTER //
// ./src/customer/create-account/create-account-identifier.js